import { Table, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import {
	DELIVERY_OPERATORS_NAMES,
	DeliveryOperator,
	VAT_CORRECTIONS_SCHEME_NAMES,
	VatCorrectionsScheme,
} from 'redux/reducers/vatCorrections/constants';
import { getExchangeRate, getGbpExchangeRate } from 'redux/reducers/vatCorrections/selectors';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { OrderCurrency } from 'ui/OrderCurrency';

export type TStep4Props = {
	externalOrderId: string | null;
	fileName: string | null;
	fileSize: number | null;
	typeOfReport: VatCorrectionsScheme;
	selectedCountry: string | null;
	selectedCurrency: string | null;
	selectedDate: {
		year: number;
		month?: number;
		quarter?: number;
	} | null;
	correctionType: 'Add' | 'Deduct' | null;
	amount: number | null;
	deliveryOperator: DeliveryOperator | null;
};
export const Step4: FC<TStep4Props> = ({
	externalOrderId,
	fileName,
	fileSize,
	typeOfReport,
	selectedCountry,
	selectedCurrency,
	selectedDate,
	deliveryOperator,
	correctionType,
	amount,
}) => {
	const exchangeRateEUR = useSelector(getExchangeRate);
	const exchangeRateGBP = useSelector(getGbpExchangeRate);
	return (
		<TableContainer
			sx={{
				'& .MuiTableCell-root': {
					border: 'none',
				},
			}}
		>
			<Table>
				<TableRow>
					<TableCell
						sx={{
							color: '#666',
							p: '10px',
						}}
					>
						Related Order #:
					</TableCell>{' '}
					<TableCell sx={{ fontWeight: 'bold', color: '#016193', p: '10px' }}>
						{externalOrderId ?? '—'}
					</TableCell>
				</TableRow>

				<TableRow>
					<TableCell
						sx={{
							color: '#666',
							p: '10px',
						}}
					>
						Report Type:
					</TableCell>{' '}
					<TableCell sx={{ fontWeight: 'bold', color: '#016193', p: '10px' }}>
						{VAT_CORRECTIONS_SCHEME_NAMES[typeOfReport]}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell
						sx={{
							color: '#666',
							p: '10px',
						}}
					>
						Country:
					</TableCell>
					<TableCell sx={{ fontWeight: 'bold', color: '#016193', p: '10px' }}>
						{selectedCountry && <CountryWithFlag countryCode={selectedCountry} bold />}
					</TableCell>
				</TableRow>
				{/* <Box
				sx={{
					fontSize: '1.1rem',
					color: '#666',
					mb: '10px',
				}}
			>
				Currency: <span style={{ fontWeight: 'bold', color: '#016193' }}>{selectedCurrency}</span>
			</Box> */}
				<TableRow>
					<TableCell
						sx={{
							color: '#666',
							p: '10px',
						}}
					>
						Sales Period:
					</TableCell>
					<TableCell
						sx={{
							p: '10px',
						}}
					>
						<span style={{ fontWeight: 'bold', color: '#016193' }}>
							{DateTime.fromObject({ month: selectedDate?.month }).toFormat('MMMM')},{' '}
							{selectedDate?.year}
						</span>
					</TableCell>
				</TableRow>
				{/* <TableRow>
					<TableCell
						sx={{
							color: '#666',
						}}
					>
						Correction Type:
					</TableCell>
					<TableCell style={{ fontWeight: 'bold', color: '#016193' }}>{correctionType}</TableCell>
				</TableRow> */}
				<TableRow>
					<TableCell
						sx={{
							color: '#666',
							p: '10px',
						}}
					>
						Amount:
					</TableCell>{' '}
					<TableCell
						sx={{
							p: '10px',
							fontWeight: 'bold',
						}}
					>
						{correctionType === 'Add' ? '' : '-'}
						<AmountAndCurrency
							amount={amount ?? 0}
							currency={selectedCurrency ?? 'EUR'}
							color="primary"
							bold
						/>{' '}
						{exchangeRateEUR &&
							typeOfReport !== VatCorrectionsScheme.NON_EU_IMPORT &&
							selectedCurrency !== 'EUR' && (
								<Typography component="span" color="primary" sx={{ fontWeight: 'bold' }}>
									({correctionType === 'Add' ? '' : '-'}
									<AmountAndCurrency
										amount={(exchangeRateEUR ?? 1) * (amount ?? 0)}
										currency="EUR"
										color="primary"
										bold
									/>
									)
								</Typography>
							)}
						{exchangeRateGBP &&
							typeOfReport === VatCorrectionsScheme.NON_EU_IMPORT &&
							selectedCurrency !== 'GBP' && (
								<Typography component="span" color="primary" sx={{ fontWeight: 'bold' }}>
									({correctionType === 'Add' ? '' : '-'}
									<AmountAndCurrency
										amount={(exchangeRateGBP ?? 1) * (amount ?? 0)}
										currency="GBP"
										color="primary"
										bold
									/>
									)
								</Typography>
							)}
					</TableCell>
				</TableRow>

				<TableRow>
					<TableCell
						sx={{
							color: '#666',
							p: '10px',
						}}
					>
						Delivery Operator:
					</TableCell>{' '}
					<TableCell sx={{ fontWeight: 'bold', color: '#016193', p: '10px' }}>
						{deliveryOperator ? DELIVERY_OPERATORS_NAMES[deliveryOperator] : '—'}
					</TableCell>
				</TableRow>

				{fileSize && (
					<TableRow>
						<TableCell
							sx={{
								color: '#666',
								p: '10px',
							}}
						>
							Attached File:
						</TableCell>
						<TableCell
							sx={{
								p: '10px',
							}}
						>
							<span style={{ fontWeight: 'bold', color: '#016193' }}>
								<b>{fileName}</b>,{' '}
								{fileSize > 1_000_000 ? (
									<span>{(fileSize / 1_000_000).toFixed(3)} MB </span>
								) : fileSize > 1000 ? (
									<span>{(fileSize / 1000).toFixed(3)} KB </span>
								) : (
									<span>{fileSize} B</span>
								)}
							</span>
						</TableCell>
					</TableRow>
				)}
			</Table>
		</TableContainer>
	);
};
